import React, { useState } from 'react';
import {
    Box,
    Flex,
    Text,
    Switch,
    Button,
    // Add ZaraButton import

    RangeSlider,
    RangeSliderTrack,
    RangeSliderFilledTrack,
    RangeSliderThumb,
    Input,

} from '@chakra-ui/react';
import ZaraButton from '../../custom-components/buttons/Button';


const SLIDER_COLOR = '#3182CE';
const BORDER_COLOR = '#E2E8F0';
const TEXT_COLOR = '#2D3748';

function ModeToggle({ mode, setMode, isChanging }) {
    return (
        <Box
            bgColor="gray.200"
            className="items-center justify-center rounded-md p-1 text-muted-foreground grid w-full grid-cols-2"
            mb={{ base: "4", md: "6" }}
        >
            <Button
                isDisabled={isChanging}
                onClick={mode !== 'buy' ? () => setMode('buy') : undefined}
                background={mode === 'buy' ? 'white' : 'inherit'}
                size="sm"
                w="100%"
            >
                Buy Minutes
            </Button>
            <Button
                isDisabled={isChanging}
                onClick={mode !== 'auto' ? () => setMode('auto') : undefined}
                background={mode === 'auto' ? 'white' : 'inherit'}
                size="sm"
                w="100%"
            >
                Auto Recharge
            </Button>
        </Box>
    );
}

function AutoRechargeSwitch({ enabled, onToggle }) {
    return (
        <Flex align="center" mb="2" gap="2">
            <Switch
                size="lg"
                isChecked={enabled}
                onChange={() => onToggle(!enabled)}
                colorScheme="blue"
            />
            <Text fontWeight="500" color={TEXT_COLOR}>
                Enable auto recharge
            </Text>
        </Flex>
    );
}

function Subtext() {
    return (
        <Text fontSize="sm" color="gray.500" mb="4">
            Automatically purchase minutes when you run low.
        </Text>
    );
}

function ThresholdSlider({ value, onChange }) {
    return (
        <Box mb="6">
            <Text fontWeight="500" color={TEXT_COLOR} mb="2">
                Recharge when minutes fall below:
            </Text>
            <Flex
                align="center"
                gap={{ base: "2", md: "4" }}
                flexDirection={{ base: "column", sm: "row" }}
                alignItems={{ base: "flex-start", sm: "center" }}
            >
                <RangeSlider
                    min={10}
                    max={50}
                    step={1}
                    value={[value]}
                    onChange={vals => onChange(vals[0])}
                    colorScheme="blue"
                    w={{ base: "100%", sm: "250px" }}
                >
                    <RangeSliderTrack bg={BORDER_COLOR}>
                        <RangeSliderFilledTrack bg={SLIDER_COLOR} />
                    </RangeSliderTrack>
                    <RangeSliderThumb index={0} />
                </RangeSlider>
                <Text minW="40px" textAlign="center" color={TEXT_COLOR}>
                    {value} min
                </Text>
            </Flex>
        </Box>
    );
}

function MinutesSliderWithInput({ value, onChange }) {
    const handleSlider = vals => onChange(vals[0]);

    const handleInput = e => {
        // Allow any number input, don't clamp here
        const v = e.target.value === '' ? '' : Number(e.target.value);
        onChange(v);
    };

    const handleInputBlur = e => {
        let v = Number(e.target.value);
        if (isNaN(v) || v < 60) v = 60;
        if (v > 500) v = 500;
        onChange(v);
    };

    return (
        <Box mb="4">
            <Text fontWeight="500" color={TEXT_COLOR} mb="2">
                Add minutes when triggered:
            </Text>
            <Flex
                align="center"
                gap={{ base: "2", md: "4" }}
                mb="2"
                flexDirection={{ base: "column", sm: "row" }}
                alignItems={{ base: "flex-start", sm: "center" }}
            >
                <RangeSlider
                    min={60}
                    max={500}
                    step={1}
                    value={[Number(value) || 60]}
                    onChange={handleSlider}
                    colorScheme="blue"
                    w={{ base: "100%", sm: "250px" }}
                >
                    <RangeSliderTrack bg={BORDER_COLOR}>
                        <RangeSliderFilledTrack bg={SLIDER_COLOR} />
                    </RangeSliderTrack>
                    <RangeSliderThumb index={0} />
                </RangeSlider>
                <Text minW="50px" textAlign="center" color={TEXT_COLOR}>
                    {value} min
                </Text>
            </Flex>
            <Input
                type="number"
                min={60}
                max={500}
                value={value}
                onChange={handleInput}
                onBlur={handleInputBlur}
                w={{ base: "100%", sm: "120px" }}
                size="sm"
                borderColor={BORDER_COLOR}
                color={TEXT_COLOR}
            />
        </Box>
    );
}

export default function AutoRechargeDisplay({ billDetails, onSave, mode, setMode }) {
    const [autoEnabled, setAutoEnabled] = useState(billDetails.auto_recharge_enabled);
    const [threshold, setThreshold] = useState(billDetails.recharge_threshold || 10);
    const [minutes, setMinutes] = useState(billDetails.recharge_minutes || 60);

    React.useEffect(() => {
        if (autoEnabled && mode !== 'auto') setMode('auto');
        if (!autoEnabled && mode !== 'buy') setMode('buy');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoEnabled]);

    const handleSave = () => {
        onSave({
            auto_recharge_enabled: autoEnabled,
            recharge_threshold: threshold,
            recharge_minutes: minutes,
        });
    };

    return (
        <Box
            p={{ base: "3", md: "6" }}
            mt={{ base: "3", md: "6" }}
            w="100%"
            maxW={{ base: "100%", md: "500px" }}
        >
            <ModeToggle mode={mode} setMode={setMode} />
            {mode === 'auto' && (
                <>
                    <AutoRechargeSwitch enabled={autoEnabled} onToggle={setAutoEnabled} />
                    <Subtext />
                    <ThresholdSlider value={threshold} onChange={setThreshold} />
                    <MinutesSliderWithInput value={minutes} onChange={setMinutes} />
                    <Box textAlign={{ base: "center", sm: "right" }} mt="2" w="100%">
                        <ZaraButton onClick={handleSave} width={{ base: "100%", sm: "auto" }}>
                            Save Auto Recharge settings
                        </ZaraButton>
                    </Box>
                </>
            )}
            {mode === 'buy' && (
                <Text color="gray.500" fontSize="sm">
                    Use the Buy Minutes option above to purchase minutes manually.
                </Text>
            )}
        </Box>
    );
}